import { ReactNode, useState } from 'react';
import { useRootSelector } from '../../../../../../store/hooks';
import { CoursePricingOption } from '../../../../../../store/features/coursePricing';
import {
  CourseOptionDisplayModuleSection,
  CourseOptionFilter,
} from '../../types';
import {
  CourseOptionAllFilterOption,
  CourseOptionFilterType,
  CourseOptionShowMoreAfterMapper,
} from '../../constants';
import {
  selectCoursePricingOptions,
  selectCoursePricingFilters,
} from '../selectors';
import { getFilteredRows, getSlicedRows } from '../../helpers';
import { CourseOptionDisplayModuleContext } from '../CourseOptionDisplayModuleContext';

export type CourseOptionDisplayModuleContextProviderProps = {
  children: ReactNode;
  section: CourseOptionDisplayModuleSection;
};

export function CourseOptionDisplayModuleContextProvider(
  props: CourseOptionDisplayModuleContextProviderProps,
) {
  const { section, children } = props;

  const coursePricingOptions = useRootSelector((state) =>
    selectCoursePricingOptions(state, section),
  );

  const filters = useRootSelector((state) =>
    selectCoursePricingFilters(state, section),
  );

  const { loadMoreButtonShownAfter, loadMoreButtonShown } = section;

  // Get show more after number value from mapper
  const showMoreAfter =
    CourseOptionShowMoreAfterMapper[loadMoreButtonShownAfter];

  // Check if show more button should be shown
  const showMoreAvailable =
    loadMoreButtonShown &&
    !!showMoreAfter &&
    showMoreAfter < coursePricingOptions.length;
  const [showMore, setShowMore] = useState(showMoreAvailable);

  // Get rows to display regarding show more after number
  const [rows, setRows] = useState<CoursePricingOption[]>(
    getSlicedRows(coursePricingOptions, showMoreAfter),
  );

  const [selectedFilters, setSelectedFilters] = useState({
    [CourseOptionFilterType.DELIVERY_TYPE]: CourseOptionAllFilterOption,
    [CourseOptionFilterType.LANGUAGE]: CourseOptionAllFilterOption,
    [CourseOptionFilterType.CURRENCY]: CourseOptionAllFilterOption,
  });

  const handleShowMoreClick = () => {
    setShowMore(false);

    const filteredRows = getFilteredRows(coursePricingOptions, selectedFilters);

    setRows(filteredRows);
  };

  const handleFilterChange = (
    filter: CourseOptionFilter['value'],
    filterKey: CourseOptionFilterType,
  ) => {
    const updatedFilters = { ...selectedFilters, [filterKey]: filter };

    setSelectedFilters(updatedFilters);

    const slicedRows = showMore
      ? getSlicedRows(coursePricingOptions, showMoreAfter)
      : coursePricingOptions;
    const filteredRows = getFilteredRows(slicedRows, updatedFilters);

    setRows(filteredRows);
  };

  return (
    <CourseOptionDisplayModuleContext.Provider
      value={{
        section,
        filters,
        selectedFilters,
        rows,
        showMore,
        handleShowMoreClick,
        handleFilterChange,
      }}
    >
      {children}
    </CourseOptionDisplayModuleContext.Provider>
  );
}
